import { useMemo, useRef, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney, todayStr } from "../engine/dates";
import { addManualEntry, addOnAccountPayment, deleteManualEntry, setOnAccountReconciled, updateManualEntry } from "../lib/data";
import { allocateOnAccount } from "../engine/forecast";

/**
 * Payments received against a client's balance rather than a named invoice.
 *
 * A client pays what they owe, not an invoice — £9,840 from Creams covers some
 * unknown share of eighteen open invoices. Scoro will allocate it eventually;
 * until then the forecast would keep expecting the whole balance and be wrong by
 * exactly that amount, on the day it matters most.
 */
export default function OnAccountView({ invoices, payments, manual = [], openingDate, onChanged }) {
  const [form, setForm] = useState({ company: "", amount: "", date: todayStr(), note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Only contacts with something outstanding: paying on account against a
  // client who owes nothing is a data-entry mistake, not a case to support.
  const contacts = useMemo(() => {
    const by = {};
    for (const i of invoices) {
      if (!i.company_id) continue;
      by[i.company_id] = by[i.company_id] || { id: i.company_id, name: i.company_name, outstanding: 0, count: 0 };
      by[i.company_id].outstanding += Number(i.outstanding) || 0;
      by[i.company_id].count++;
    }
    return Object.values(by).sort((a, b) => b.outstanding - a.outstanding);
  }, [invoices]);

  const allocation = useMemo(
    () => allocateOnAccount(invoices, payments || []),
    [invoices, payments]
  );

  const selected = contacts.find((c) => String(c.id) === String(form.company));

  const submit = async () => {
    const amount = Number(form.amount);
    if (!selected || !amount || amount <= 0) return;
    setBusy(true);
    setError(null);
    try {
      await addOnAccountPayment({
        scoro_company_id: selected.id,
        contact_name: selected.name,
        amount,
        paid_date: form.date,
        note: form.note || null,
        // Snapshot the balance now. Once Scoro's outstanding for this contact has
        // fallen by at least this much, Scoro has allocated it and the credit
        // must stop applying — otherwise the payment is counted twice.
        contact_outstanding_at_entry: selected.outstanding,
      });
      setForm({ company: "", amount: "", date: todayStr(), note: "" });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async (id, value) => {
    setBusy(true);
    try {
      await setOnAccountReconciled(id, value);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const supersededIds = new Set(allocation.superseded.map((p) => p.id));
  const supersededById = new Map(allocation.superseded.map((p) => [p.id, p]));

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Payments on account</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 660 }}>
        Money in against a client's balance, before Scoro has allocated it to invoices. The credit
        comes off their oldest open invoices first, so the forecast stops expecting cash that has
        already arrived. When Scoro catches up, the entry is flagged and stops applying — it can
        only reduce the forecast once.
      </p>

      <div style={{ background: T.panel, borderRadius: 10, padding: 16, margin: "14px 0", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ fontSize: 11, color: T.muted }}>
          Client
          <br />
          <select
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            style={{ fontSize: 12, padding: "5px 6px", minWidth: 240, marginTop: 2 }}
          >
            <option value="">— choose —</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {fmtMoney(c.outstanding)} open
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11, color: T.muted }}>
          Amount received
          <br />
          <input
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="9840.00"
            style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 110, marginTop: 2 }}
          />
        </label>
        <label style={{ fontSize: 11, color: T.muted }}>
          Date
          <br />
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
          />
        </label>
        <label style={{ fontSize: 11, color: T.muted, flex: 1, minWidth: 160 }}>
          Note
          <br />
          <input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="BACS, unallocated"
            style={{ fontSize: 12, padding: "5px 6px", width: "100%", marginTop: 2 }}
          />
        </label>
        <button
          onClick={submit}
          disabled={busy || !selected || !Number(form.amount)}
          style={{
            padding: "7px 16px",
            borderRadius: 6,
            border: "none",
            background: selected && Number(form.amount) ? T.accent : T.border,
            color: selected && Number(form.amount) ? "#fff" : T.muted,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Record payment
        </button>
      </div>

      {selected && Number(form.amount) > selected.outstanding && (
        <div style={{ fontSize: 11, color: T.warn, marginBottom: 10 }}>
          That's more than {selected.name} currently owes ({fmtMoney(selected.outstanding)}). The
          excess will sit as unallocated credit rather than being invented as income.
        </div>
      )}
      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "18px 0 6px" }}>Recorded</h3>
      {!payments?.length && <div style={{ fontSize: 12, color: T.muted }}>Nothing recorded yet.</div>}

      {payments?.map((p) => {
        const stale = supersededIds.has(p.id);
        return (
          <div
            key={p.id}
            style={{
              background: T.panel,
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 8,
              border: `1px solid ${stale ? T.warn : p.reconciled ? T.borderSoft : T.accent}`,
              opacity: p.reconciled ? 0.6 : 1,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 13 }}>{p.contact_name}</strong>
              <span style={{ fontFamily: fontMono, fontSize: 14, color: T.income }}>
                {fmtMoney(p.amount)}
              </span>
              <span style={{ fontSize: 11, color: T.muted }}>{fmtDay(p.paid_date)}</span>
              {p.note && <span style={{ fontSize: 11, color: T.muted }}>· {p.note}</span>}
              <label style={{ marginLeft: "auto", fontSize: 11, display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!p.reconciled}
                  disabled={busy}
                  onChange={(e) => reconcile(p.id, e.target.checked)}
                />
                Scoro has allocated this
              </label>
            </div>
            {stale && !p.reconciled && (
              <div style={{ fontSize: 11, color: T.warn, marginTop: 6 }}>
                Scoro has since recorded
                {supersededById.get(p.id)?.scoroHas != null
                  ? ` ${fmtMoney(supersededById.get(p.id).scoroHas)} received`
                  : " this money"}{" "}
                from {p.contact_name} on or after {fmtDay(p.paid_date)} — the same money, against
                its invoices. This entry has stopped counting so it is not counted twice. Tick it
                off.
              </div>
            )}
            {!stale && !p.reconciled && (
              <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
                Reducing the forecast now · balance when recorded{" "}
                {fmtMoney(p.contact_outstanding_at_entry)}
              </div>
            )}
          </div>
        );
      })}

      <ManualEntries entries={manual} openingDate={openingDate} onChanged={onChanged} />
    </div>
  );
}


/**
 * Why an entry isn't in the forecast.
 *
 * Anything dated on or before the opening balance is already inside that
 * figure, so counting it again would double it. Correct, but it vanishes
 * silently — someone adds a payment, sees nothing appear, and reasonably
 * concludes the feature is broken.
 */
function OutsideWindow({ date, openingDate }) {
  if (!openingDate || !date || date > openingDate) return null;
  return (
    <span style={{ fontSize: 10, color: T.warn }}>
      · not in the forecast — dated on or before the opening balance ({fmtDay(openingDate)}), so it
      is already inside that figure
    </span>
  );
}

/**
 * Cash no document produces — lending, grants, a VAT refund, a one-off cost.
 *
 * Everything else here traces to a Scoro document by design. These are the
 * honest exception, so they are labelled manual rather than quietly mixed in,
 * and each says whether the money has actually landed.
 */
/** One field on an editable row. */
const rowField = (width) => ({
  fontFamily: "inherit",
  fontSize: 12,
  padding: "4px 6px",
  width,
  minWidth: 0,
  borderRadius: 5,
  border: "1px solid #E0D8C8",
});

function ManualEntries({ entries, openingDate, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const draft = useRef({});

  const blank = {
    label: "",
    category: "lending",
    direction: "in",
    amount: "",
    entry_date: todayStr(),
    note: "",
    received: false,
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const save = async () => {
    if (!form.label.trim() || !Number(form.amount)) return;
    setBusy(true);
    setError(null);
    try {
      await addManualEntry({
        label: form.label.trim(),
        category: form.category,
        direction: form.direction,
        amount: Number(form.amount),
        entry_date: form.entry_date,
        note: form.note || null,
        received: form.received,
      });
      setForm(blank);
      setOpen(false);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, p) => {
    setBusy(true);
    try {
      await updateManualEntry(id, p);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await deleteManualEntry(id);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const lbl = { fontSize: 11, color: T.muted };

  return (
    <div style={{ marginTop: 28 }}>
      <HmrcPayments entries={entries.filter((e) => e.label === "HMRC")} openingDate={openingDate} onChanged={onChanged} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 28 }}>
        <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: 0 }}>Other income and costs</h3>
        <button
          onClick={() => setOpen(!open)}
          style={{
            marginLeft: "auto",
            border: `1px solid ${T.border}`,
            background: "transparent",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {open ? "Cancel" : "+ Add entry"}
        </button>
      </div>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 660, marginTop: 4 }}>
        Money with no invoice or quote behind it — lending, grants, a VAT refund, a one-off cost.
        Each gets its own row in the forecast under the label you give it, so "Lending" reads as
        Lending. Tick "already received" for money that has landed and it shows green rather than
        as something still to come.
      </p>

      {open && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 14, margin: "10px 0", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={lbl}>
            Label (becomes the row)
            <br />
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Lending"
              style={{ fontSize: 12, padding: "5px 6px", width: 150, marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            In or out
            <br />
            <select
              value={form.direction}
              onChange={(e) => setForm({ ...form, direction: e.target.value })}
              style={{ fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            >
              <option value="in">Money in</option>
              <option value="out">Money out</option>
            </select>
          </label>
          <label style={lbl}>
            Category
            <br />
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              style={{ fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            >
              {["lending", "grant", "tax", "staff", "suppliers", "software", "other"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label style={lbl}>
            Amount
            <br />
            <input
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="50000"
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 100, marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            Date
            <br />
            <input
              type="date"
              value={form.entry_date}
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            />
          </label>
          <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 5 }}>
            <input
              type="checkbox"
              checked={form.received}
              onChange={(e) => setForm({ ...form, received: e.target.checked })}
            />
            {form.direction === "out" ? "already paid" : "already received"}
          </label>
          <button
            onClick={save}
            disabled={busy || !form.label.trim() || !Number(form.amount)}
            style={{
              padding: "7px 16px",
              borderRadius: 6,
              border: "none",
              background: form.label.trim() && Number(form.amount) ? T.accent : T.border,
              color: form.label.trim() && Number(form.amount) ? "#fff" : T.muted,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Add
          </button>
        </div>
      )}

      {error && <div style={{ color: T.cost, fontSize: 12 }}>{error}</div>}

      {!entries.length && (
        <div style={{ fontSize: 12, color: T.muted }}>No manual entries yet.</div>
      )}

      {entries.filter((m) => m.label !== "HMRC").map((m) => (
        <div
          key={m.id}
          style={{
            background: T.panel,
            borderRadius: 8,
            padding: "9px 14px",
            marginBottom: 8,
            border: `1px solid ${m.received ? T.received : T.borderSoft}`,
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {editing === m.id ? (
            /* Every one of these was typed by somebody, so every one of them
               can be mistyped. Until now the only remedies were to leave it
               wrong or delete it, and deleting a line to correct it throws away
               the note explaining why it was there. */
            <>
              <input
                defaultValue={m.label || ""}
                onChange={(e) => (draft.current.label = e.target.value)}
                placeholder="What it is"
                style={rowField(200)}
              />
              <input
                defaultValue={Math.round(Math.abs(Number(m.amount) || 0))}
                onChange={(e) => (draft.current.amount = e.target.value)}
                style={{ ...rowField(100), fontFamily: fontMono, textAlign: "right" }}
              />
              <input
                type="date"
                defaultValue={m.entry_date || ""}
                onChange={(e) => (draft.current.entry_date = e.target.value)}
                style={{ ...rowField(150), fontFamily: fontMono }}
              />
              <select
                defaultValue={m.category || "other"}
                onChange={(e) => (draft.current.category = e.target.value)}
                style={rowField(130)}
              >
                {["income", "suppliers", "media", "tax", "debt", "lending", "payroll", "other"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {m.direction === "in" && (
                <select
                  defaultValue={m.vat_rate == null ? "" : String(m.vat_rate)}
                  onChange={(e) => (draft.current.vat_rate = e.target.value)}
                  title="Left alone, the rate is read from what this client is actually invoiced"
                  style={rowField(150)}
                >
                  <option value="">VAT: as they're invoiced</option>
                  <option value="20">VAT: 20%</option>
                  <option value="0">VAT: none (zero-rated)</option>
                </select>
              )}
              <input
                defaultValue={m.note || ""}
                onChange={(e) => (draft.current.note = e.target.value)}
                placeholder="Why, for whoever reads this later"
                style={{ ...rowField(220), flex: 1 }}
              />
              <button
                disabled={busy}
                onClick={async () => {
                  const dr = draft.current;
                  const up = {};
                  if (dr.label != null && dr.label.trim()) up.label = dr.label.trim();
                  if (dr.amount != null && Number(dr.amount)) up.amount = Math.abs(Number(dr.amount));
                  if (dr.entry_date) up.entry_date = dr.entry_date;
                  if (dr.category) up.category = dr.category;
                  if (dr.note != null) up.note = dr.note.trim() || null;
                  // An empty choice means "follow the client", which is a real
                  // setting rather than an absent one.
                  if (dr.vat_rate !== undefined) {
                    up.vat_rate = dr.vat_rate === "" ? null : Number(dr.vat_rate);
                  }
                  if (!Object.keys(up).length) return setEditing(null);
                  await patch(m.id, up);
                  setEditing(null);
                }}
                style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: T.accent, color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                Save
              </button>
              <button
                onClick={() => setEditing(null)}
                style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                cancel
              </button>
            </>
          ) : (
            <>
              <strong style={{ fontSize: 13 }}>{m.label}</strong>
              <span
                style={{
                  fontFamily: fontMono,
                  fontSize: 13,
                  color: m.direction === "in" ? T.income : T.cost,
                }}
              >
                {m.direction === "in" ? "" : "-"}
                {fmtMoney(m.amount)}
              </span>
              <span style={{ fontSize: 11, color: T.muted }}>
                {fmtDay(m.entry_date)} · {m.category}
              </span>
              {m.note && <span style={{ fontSize: 11, color: T.muted }}>· {m.note}</span>}
              <OutsideWindow date={m.entry_date} openingDate={openingDate} />
              <button
                onClick={() => {
                  draft.current = {};
                  setEditing(m.id);
                }}
                title="Change this entry"
                style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", padding: 0 }}
              >
                edit
              </button>
              <label style={{ marginLeft: "auto", fontSize: 11, display: "flex", gap: 5, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!m.received}
                  disabled={busy}
                  onChange={(e) => patch(m.id, { received: e.target.checked })}
                />
                {m.direction === "out" ? "already paid" : "already received"}
              </label>
              <button
                onClick={() => (confirming === m.id ? remove(m.id) : setConfirming(m.id))}
                disabled={busy}
                title={confirming === m.id ? "Press again to remove it" : "Remove this entry"}
                style={{ border: "none", background: "none", color: confirming === m.id ? T.cost : T.muted, cursor: "pointer", fontSize: confirming === m.id ? 11 : 14, fontFamily: "inherit" }}
              >
                {confirming === m.id ? "really remove?" : "×"}
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}


/**
 * HMRC.
 *
 * One row in the forecast, three obligations underneath. PAYE, VAT and
 * corporation tax fall due on different cycles and are worth telling apart when
 * looking at what is coming, but they are one payee and belong on one line.
 */
function HmrcPayments({ entries, openingDate, onChanged }) {
  // Which row is open for editing, and what has been typed into it. The draft
  // is a ref rather than state so a keystroke does not redraw the whole list.
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const draft = useRef({});

  const [form, setForm] = useState({
    subtype: "PAYE",
    amount: "",
    entry_date: todayStr(),
    note: "",
    received: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!Number(form.amount)) return;
    setBusy(true);
    setError(null);
    try {
      await addManualEntry({
        label: "HMRC",
        category: "tax",
        subtype: form.subtype,
        direction: "out",
        amount: Number(form.amount),
        entry_date: form.entry_date,
        note: form.note || null,
        received: form.received,
        own_row: true,
      });
      setForm({ ...form, amount: "", note: "" });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const lbl = { fontSize: 11, color: T.muted };
  const due = entries.filter((e) => !e.received).reduce((s, e) => s + Number(e.amount), 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: 0 }}>HMRC</h3>
        {due > 0 && (
          <span style={{ fontFamily: fontMono, fontSize: 13, color: T.cost }}>
            {fmtMoney(due)} still to pay
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 660, marginTop: 4 }}>
        PAYE, VAT and corporation tax. One HMRC row in the forecast, with each payment kept
        separate underneath so you can see which is which.
      </p>

      <div style={{ background: T.panel, borderRadius: 10, padding: 14, margin: "10px 0", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={lbl}>
          Type
          <br />
          <select
            value={form.subtype}
            onChange={(e) => setForm({ ...form, subtype: e.target.value })}
            style={{ fontSize: 12, padding: "5px 6px", marginTop: 2 }}
          >
            <option value="PAYE">PAYE</option>
            <option value="VAT">VAT</option>
            <option value="CT">Corporation tax</option>
          </select>
        </label>
        <label style={lbl}>
          Amount
          <br />
          <input
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="14150"
            style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 100, marginTop: 2 }}
          />
        </label>
        <label style={lbl}>
          Date
          <br />
          <input
            type="date"
            value={form.entry_date}
            onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
            style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
          />
        </label>
        <label style={{ ...lbl, flex: 1, minWidth: 150 }}>
          Note
          <br />
          <input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Q2 return"
            style={{ fontSize: 12, padding: "5px 6px", width: "100%", marginTop: 2 }}
          />
        </label>
        <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 5 }}>
          <input
            type="checkbox"
            checked={form.received}
            onChange={(e) => setForm({ ...form, received: e.target.checked })}
          />
          already paid
        </label>
        <button
          onClick={save}
          disabled={busy || !Number(form.amount)}
          style={{
            padding: "7px 16px",
            borderRadius: 6,
            border: "none",
            background: Number(form.amount) ? T.accent : T.border,
            color: Number(form.amount) ? "#fff" : T.muted,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Add
        </button>
      </div>

      {error && <div style={{ color: T.cost, fontSize: 12 }}>{error}</div>}

      {entries.map((m) => (
        <div
          key={m.id}
          style={{
            background: T.panel,
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 6,
            border: `1px solid ${m.received ? T.settled : T.borderSoft}`,
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            fontSize: 12,
          }}
        >
          {editing === m.id ? (
            /* Typed in by hand, so wrong by hand too — a figure entered in a
               hurry had only two outcomes here, leave it or delete it, and
               deleting a line to change it loses the reason it was added. */
            <>
              <input
                defaultValue={m.label || ""}
                onChange={(e) => (draft.current.label = e.target.value)}
                placeholder="What it is"
                style={{ fontFamily: "inherit", fontSize: 12, padding: "4px 6px", width: 190, borderRadius: 5, border: `1px solid ${T.border}` }}
              />
              <input
                defaultValue={Math.round(Math.abs(Number(m.amount) || 0))}
                onChange={(e) => (draft.current.amount = e.target.value)}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "4px 6px", width: 96, textAlign: "right", borderRadius: 5, border: `1px solid ${T.border}` }}
              />
              <input
                type="date"
                defaultValue={m.entry_date || ""}
                onChange={(e) => (draft.current.entry_date = e.target.value)}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "4px 6px", borderRadius: 5, border: `1px solid ${T.border}` }}
              />
              <input
                defaultValue={m.note || ""}
                onChange={(e) => (draft.current.note = e.target.value)}
                placeholder="Why, for whoever reads this later"
                style={{ fontFamily: "inherit", fontSize: 12, padding: "4px 6px", minWidth: 210, flex: 1, borderRadius: 5, border: `1px solid ${T.border}` }}
              />
              <button
                disabled={busy}
                onClick={async () => {
                  const d = draft.current;
                  const patch = {};
                  if (d.label != null && d.label.trim()) patch.label = d.label.trim();
                  if (d.amount != null && Number(d.amount)) patch.amount = Math.abs(Number(d.amount));
                  if (d.entry_date) patch.entry_date = d.entry_date;
                  if (d.note != null) patch.note = d.note.trim() || null;
                  if (!Object.keys(patch).length) return setEditing(null);
                  setBusy(true);
                  try {
                    await updateManualEntry(m.id, patch);
                    setEditing(null);
                    await onChanged();
                  } catch (err) {
                    setError(err.message || String(err));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: T.accent, color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                Save
              </button>
              <button
                onClick={() => setEditing(null)}
                style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                cancel
              </button>
            </>
          ) : (
            <>
              <strong style={{ minWidth: 46 }}>{m.subtype || m.label || "—"}</strong>
              <span style={{ fontFamily: fontMono, color: m.received ? T.settled : T.cost }}>
                {fmtMoney(m.amount)}
              </span>
              <span style={{ fontSize: 11, color: T.muted }}>{fmtDay(m.entry_date)}</span>
              {m.note && <span style={{ fontSize: 11, color: T.muted }}>· {m.note}</span>}
              <OutsideWindow date={m.entry_date} openingDate={openingDate} />
              <button
                onClick={() => {
                  draft.current = {};
                  setEditing(m.id);
                }}
                title="Change this entry"
                style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", padding: 0 }}
              >
                edit
              </button>
            </>
          )}
          {editing !== m.id && (
            <>
              <label style={{ marginLeft: "auto", fontSize: 11, display: "flex", gap: 5, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!m.received}
                  disabled={busy}
                  onChange={async (e) => {
                    setBusy(true);
                    await updateManualEntry(m.id, { received: e.target.checked });
                    await onChanged();
                    setBusy(false);
                  }}
                />
                {m.direction === "out" ? "already paid" : "already received"}
              </label>
              <button
                onClick={async () => {
                  if (confirming !== m.id) return setConfirming(m.id);
                  setBusy(true);
                  await deleteManualEntry(m.id);
                  setConfirming(null);
                  await onChanged();
                  setBusy(false);
                }}
                title={confirming === m.id ? "Press again to remove it" : "Remove this entry"}
                style={{ border: "none", background: "none", color: confirming === m.id ? T.cost : T.muted, cursor: "pointer", fontSize: confirming === m.id ? 11 : 14, fontFamily: "inherit" }}
              >
                {confirming === m.id ? "really remove?" : "×"}
              </button>
            </>
          )}
        </div>
      ))}
      {!entries.length && (
        <div style={{ fontSize: 12, color: T.muted }}>Nothing recorded yet.</div>
      )}
    </div>
  );
}
